import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import {
  EmployeeRequestResponsibleForAnswerRequestableStatuses,
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';
import { WorkingContractWeeklyScheduleExceptionRequest } from 'src/app/models/working-contract-weekly-schedule-exception-request.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import {
  formatMadridWeekRange,
  WorkingContractWeeklyScheduleExceptionRequestDetailDialogComponent,
} from './working-contract-weekly-schedule-exception-request-detail-dialog/working-contract-weekly-schedule-exception-request-detail-dialog.component';

@Component({
  selector: 'app-working-contract-weekly-schedule-exception-requests',
  templateUrl: './working-contract-weekly-schedule-exception-requests.component.html',
  styleUrls: ['./working-contract-weekly-schedule-exception-requests.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TypeSafeMatCellDeferred,
  ],
})
export class WorkingContractWeeklyScheduleExceptionRequestsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  readonly displayed_columns: string[] = ['user_name', 'week', 'reason', 'status', 'actions'];

  readonly data_source = new MatTableDataSource<WorkingContractWeeklyScheduleExceptionRequestSerialized>([]);

  readonly status_options = EmployeeRequestResponsibleForAnswerRequestableStatuses.map(status => ({
    label: EmployeeRequestResponsibleForAnswerStatusLabels[status],
    status,
  }));

  private readonly api = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly user = inject(UserService);
  private readonly permissions = inject(PermissionsService);
  private readonly dialog = inject(MatDialog);

  protected readonly current_user_id = this.user.getCurrentUser()?.id;
  protected readonly is_rrhh_or_admin = toSignal(this.permissions.validateDepartmentObservable(['direction', 'rrhh']), {
    initialValue: false,
  });

  constructor() {
    const router = inject(Router);
    const fb = inject(UntypedFormBuilder);
    const activated_route = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activated_route, utils);

    const responsible_user_id_from_query = activated_route.snapshot.queryParamMap.get('responsible_user_id');
    const status_from_query = activated_route.snapshot.queryParamMap.get('status');
    const user_id_from_query = activated_route.snapshot.queryParamMap.get('user_id');
    const responsible_user_id =
      responsible_user_id_from_query === null
        ? (this.current_user_id ?? '')
        : responsible_user_id_from_query === ''
          ? ''
          : Number(responsible_user_id_from_query);

    this.filterFormExtraParams = {
      status: [status_from_query ?? EmployeeRequestResponsibleForAnswerStatusEnum.PENDING],
      user_id: [user_id_from_query !== null ? Number(user_id_from_query) : ''],
      responsible_user_id: [responsible_user_id],
      with: ['user,working_contract_weekly_schedule_exception'],
    };
    if (responsible_user_id_from_query === null && this.current_user_id !== undefined) {
      void this.router.navigate([], {
        relativeTo: activated_route,
        queryParams: {
          responsible_user_id: this.current_user_id,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  override fetchData(): void {
    this.api
      .getWorkingContractWeeklyScheduleExceptionRequests({
        ...this.filterForm.value,
        order_by: 'created_at:desc',
      })
      .subscribe(data => {
        this.data_source.data = this.serializeRequests(data.data);
        this.setMetadata(Number(data.meta.total), Number(data.meta.current_page) - 1, Number(data.meta.per_page));
        this.change_detector_ref.markForCheck();
      });
  }

  openDetail(request: WorkingContractWeeklyScheduleExceptionRequestSerialized): void {
    const exception = request.working_contract_weekly_schedule_exception!;

    this.dialog
      .open(WorkingContractWeeklyScheduleExceptionRequestDetailDialogComponent, {
        width: '1000px',
        maxWidth: '95vw',
        autoFocus: false,
        data: {
          id: request.id,
          user_id: request.user_id,
          created_at: request.created_at,
          start_week_date: exception.start_week_date,
          end_week_date: exception.end_week_date,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((changed: boolean | undefined): void => {
        if (changed === true) {
          this.fetchData();
        }
      });
  }

  openApprovalStatusDialog(request: WorkingContractWeeklyScheduleExceptionRequestSerialized): void {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.WorkingContractWeeklyScheduleExceptionRequest,
        requestable_id: request.id,
        requestable_status: request.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }

  private serializeRequests(
    requests: WorkingContractWeeklyScheduleExceptionRequest[]
  ): WorkingContractWeeklyScheduleExceptionRequestSerialized[] {
    return requests.map(
      (request): WorkingContractWeeklyScheduleExceptionRequestSerialized => ({
        ...request,
        user_name: request.user?.full_name ?? '',
        week_label: formatMadridWeekRange(
          request.working_contract_weekly_schedule_exception!.start_week_date,
          request.working_contract_weekly_schedule_exception!.end_week_date
        ),
        reason_truncated: truncateReason(request.reason),
        status_label: EmployeeRequestResponsibleForAnswerStatusLabels[request.status],
      })
    );
  }
}

interface WorkingContractWeeklyScheduleExceptionRequestSerialized extends WorkingContractWeeklyScheduleExceptionRequest {
  user_name: string;
  week_label: string;
  reason_truncated: string;
  status_label: string;
}

function truncateReason(reason: string, max_length = 60): string {
  return reason.length > max_length ? `${reason.slice(0, max_length).trimEnd()}…` : reason;
}
